import * as React from 'react';
import { useMemo, useState } from 'react';
import moment from 'moment-timezone';
import { eventDay } from '@/src/v2/lib/eventStats';
import { Field, TextInput } from '../../../components/forms';
import { Segmented } from '../../../components/table';
import { IconSearch } from '../../../chrome/icons';
import { normalizeTag } from '../gallery/parts';
import { destinationError, EMAIL_RE, plural, Scope, scopeNote, Sheet } from './parts';

/* The selection's outbound actions. Each sheet only collects input;
   the page shows the confirm and makes the call. */

function ScopeLine({ scope }: { scope: Scope }) {
    const note = scopeNote(scope);
    return note ? <p className="med-note">{note}</p> : null;
}

export function ShareSheet({
    scope,
    galleryReady,
    onClose,
    onReview,
}: {
    scope: Scope;
    galleryReady: boolean;
    onClose: () => void;
    onReview: (dest: string) => void;
}) {
    const [dest, setDest] = useState('');
    const [touched, setTouched] = useState(false);
    const error = destinationError(dest);
    const submit = () => {
        setTouched(true);
        if (error || !galleryReady || scope.ids.length === 0) return;
        onReview(dest.trim());
    };
    return (
        <Sheet
            open
            title={`Share ${plural(scope.picked, 'item')}`}
            onClose={onClose}
            onSubmit={submit}
            footer={
                <>
                    <button type="button" className="tb-btn" onClick={onClose}>
                        Cancel
                    </button>
                    <button type="submit" className="tb-new" disabled={!galleryReady || scope.ids.length === 0}>
                        Review
                    </button>
                </>
            }
        >
            <Field label="Send to" error={touched ? error : undefined} help="An email address, or a mobile number for a text.">
                <TextInput
                    autoFocus
                    inputMode="email"
                    placeholder="guest@example.com or +1 555 123 4567"
                    value={dest}
                    aria-invalid={touched && error ? true : undefined}
                    onChange={(e) => setDest(e.target.value)}
                />
            </Field>
            {!galleryReady && <p className="field-e">This event has no gallery yet, so there’s nothing to share from.</p>}
            {scope.ids.length === 0 && <p className="field-e">None of the selected items have a share ID.</p>}
            <ScopeLine scope={scope} />
        </Sheet>
    );
}

export function ZipSheet({
    scope,
    label,
    onClose,
    onReview,
}: {
    scope: Scope;
    /** what is being zipped, e.g. "14 selected" */
    label: string;
    onClose: () => void;
    onReview: (email: string) => void;
}) {
    const [email, setEmail] = useState('');
    const [touched, setTouched] = useState(false);
    const error = !email.trim() ? 'Enter the email address for the download link.' : EMAIL_RE.test(email.trim()) ? undefined : 'Enter a valid email address.';
    const submit = () => {
        setTouched(true);
        if (error || scope.ids.length === 0) return;
        onReview(email.trim());
    };
    return (
        <Sheet
            open
            title={`Download ${label}`}
            onClose={onClose}
            onSubmit={submit}
            footer={
                <>
                    <button type="button" className="tb-btn" onClick={onClose}>
                        Cancel
                    </button>
                    <button type="submit" className="tb-new" disabled={scope.ids.length === 0}>
                        Review
                    </button>
                </>
            }
        >
            <p>We build a zip of the originals and email a download link. Large exports split at 5,000 photos.</p>
            <Field label="Email the link to" error={touched ? error : undefined}>
                <TextInput
                    autoFocus
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    aria-invalid={touched && error ? true : undefined}
                    onChange={(e) => setEmail(e.target.value)}
                />
            </Field>
            {scope.ids.length === 0 && <p className="field-e">Nothing here has a share ID to zip.</p>}
            <ScopeLine scope={scope} />
        </Sheet>
    );
}

export function CopySheet({
    scope,
    targets,
    failed,
    onRetry,
    onClose,
    onPick,
}: {
    scope: Scope;
    targets: any[] | null;
    failed: boolean;
    onRetry: () => void;
    onClose: () => void;
    onPick: (target: any) => void;
}) {
    const [q, setQ] = useState('');
    const rows = useMemo(
        () => (targets ?? []).filter((a) => `${a.name ?? ''}`.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 30),
        [targets, q],
    );
    return (
        <Sheet
            open
            title={`Copy ${plural(scope.picked, 'item')} to…`}
            onClose={onClose}
            footer={
                <button type="button" className="tb-btn" onClick={onClose}>
                    Cancel
                </button>
            }
        >
            <label className="tb-search med-search">
                <IconSearch />
                <input autoFocus value={q} placeholder="Search events" spellCheck={false} autoComplete="off" onChange={(e) => setQ(e.target.value)} />
            </label>
            <div className="med-list med-pick">
                {failed && (
                    <p className="field-e">
                        Couldn’t load events.{' '}
                        <button type="button" className="tb-btn sm" onClick={onRetry}>
                            Retry
                        </button>
                    </p>
                )}
                {targets == null && !failed && <p className="quiet-empty">Loading…</p>}
                {targets != null && rows.length === 0 && <p className="quiet-empty">No events match.</p>}
                {rows.map((a) => (
                    <button key={a.id} type="button" className="med-li med-li-b" onClick={() => onPick(a)}>
                        <span className="nm">{a.name}</span>
                        <span className="sm">{a.date ? eventDay(a.date).format('MMM D, YYYY') : ''}</span>
                    </button>
                ))}
            </div>
            {scope.ids.length === 0 && <p className="field-e">None of the selected items have a share ID.</p>}
            <ScopeLine scope={scope} />
        </Sheet>
    );
}

export function TagSheet({
    scope,
    activationTags,
    selectionTags,
    onClose,
    onReview,
}: {
    scope: Scope;
    activationTags: string[];
    selectionTags: string[];
    onClose: () => void;
    onReview: (mode: 'add' | 'remove', tags: string[]) => void;
}) {
    const [mode, setMode] = useState<'add' | 'remove'>('add');
    const [pending, setPending] = useState<string[]>([]);
    const [draft, setDraft] = useState('');

    const push = () => {
        const t = normalizeTag(draft);
        setDraft('');
        if (!t || pending.includes(t)) return;
        setPending((p) => [...p, t]);
    };
    const all = () => {
        const t = normalizeTag(draft);
        return t && !pending.includes(t) ? [...pending, t] : pending;
    };
    const suggestions = (mode === 'add' ? activationTags : selectionTags).filter((t) => !pending.includes(t)).slice(0, 12);
    const submit = () => {
        const tags = all();
        if (tags.length === 0 || scope.ids.length === 0) return;
        onReview(mode, tags);
    };

    return (
        <Sheet
            open
            title={`Tag ${plural(scope.picked, 'item')}`}
            onClose={onClose}
            footer={
                <>
                    <button type="button" className="tb-btn" onClick={onClose}>
                        Cancel
                    </button>
                    <button type="button" className={mode === 'remove' ? 'tb-new danger' : 'tb-new'} disabled={all().length === 0 || scope.ids.length === 0} onClick={submit}>
                        Review
                    </button>
                </>
            }
        >
            <Segmented<'add' | 'remove'>
                label="Tag action"
                value={mode}
                onChange={(v) => {
                    setMode(v);
                    setPending([]);
                }}
                options={[
                    { value: 'add', label: 'Add tags' },
                    { value: 'remove', label: 'Remove tags' },
                ]}
            />
            <div className="med-chips ev-gap">
                {pending.map((t) => (
                    <span key={t} className="pchip med-ptag">
                        {t}
                        <button type="button" aria-label={`Drop ${t}`} onClick={() => setPending((p) => p.filter((x) => x !== t))}>
                            ×
                        </button>
                    </span>
                ))}
                <TextInput
                    autoFocus
                    className="input med-tag-in"
                    aria-label="Tag"
                    placeholder="Type a tag, Enter"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            if (!draft.trim() && pending.length > 0) submit();
                            else push();
                        }
                    }}
                />
            </div>
            {suggestions.length > 0 && (
                <div className="med-chips med-sugg" aria-label={mode === 'add' ? 'Tags used in this event' : 'Tags on the selection'}>
                    {suggestions.map((t) => (
                        <button key={t} type="button" onClick={() => setPending((p) => [...p, t])}>
                            + {t}
                        </button>
                    ))}
                </div>
            )}
            {mode === 'remove' && <p className="med-note">Removes these tags from every selected item that has them.</p>}
            {scope.ids.length === 0 && <p className="field-e">None of the selected items have a share ID.</p>}
            <ScopeLine scope={scope} />
        </Sheet>
    );
}

export function ZipRequests({ rows }: { rows: any[] }) {
    return (
        <div className="med-list med-zips">
            {rows.map((r: any, i: number) => (
                <div key={r.id ?? i} className="med-li">
                    <span className="t">
                        <span className="nm med-trunc">{r.zipRequestDestination ?? '—'}</span>
                        <span className="sm">{r.createdAt ? `${moment(r.createdAt).fromNow()}` : ''}</span>
                    </span>
                    <span className={r.zipRequestUri ? 'ev-st ok' : 'ev-st muted'}>{r.zipRequestUri ? 'Ready' : r.status ?? 'Processing'}</span>
                    {r.zipRequestUri && (
                        <a className="tb-btn sm" href={r.zipRequestUri} target="_blank" rel="noreferrer">
                            Download
                        </a>
                    )}
                </div>
            ))}
        </div>
    );
}
